import { useCallback, useEffect, useRef } from "react";
import type { WebSocketClient } from "./client";
import { type ConnectionState, useWebSocketContext } from "./context";
import type { IncomingWebSocketEvent } from "./types";

/** Get the WebSocket client instance */
export function useWebSocketClient(): WebSocketClient | null {
  const { client } = useWebSocketContext();
  return client;
}

/** Get the current connection state */
export function useWebSocketConnection(): {
  isConnected: boolean;
  connectionState: ConnectionState;
} {
  const { isConnected, connectionState } = useWebSocketContext();
  return { isConnected, connectionState };
}

/** Subscribe to a WebSocket event with automatic cleanup */
export function useWebSocketEvent<T extends IncomingWebSocketEvent["type"]>(
  event: T,
  callback: (data: Extract<IncomingWebSocketEvent, { type: T }>["payload"]) => void,
  deps: React.DependencyList = [],
) {
  const { client } = useWebSocketContext();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const callbackRef = useRef<(data: any) => void>(callback);
  callbackRef.current = callback;

  useEffect(() => {
    if (!client) {
      return;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const handler = (data: any) => {
      callbackRef.current(data);
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    client.on(event, handler as any);

    return () => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      client.off(event, handler as any);
    };
  }, [client, event, ...deps]);
}

/** Send a ping and track connection health */
export function useWebSocketPing() {
  const { client, isConnected } = useWebSocketContext();

  const ping = useCallback(() => {
    if (client && isConnected) {
      client.ping();
    }
  }, [client, isConnected]);

  return { ping, isConnected };
}
